import {
  RECOMMENDED_SIGNAL_SUBSCRIPTIONS,
  type SignalPriority,
  type SignalType,
  type SignalTypeGroup,
} from '@parcel/types';

/** Presence = selected; value = the chosen priority. */
export type SignalSelections = Map<SignalType, SignalPriority>;

const RECOMMENDED_PRIORITY = new Map<SignalType, SignalPriority>(
  RECOMMENDED_SIGNAL_SUBSCRIPTIONS.map((r) => [r.signalType, r.priority]),
);

/** A newly-checked type starts at its registry-recommended priority, else medium. */
export function defaultPriorityFor(signalType: SignalType): SignalPriority {
  return RECOMMENDED_PRIORITY.get(signalType) ?? 'medium';
}

/** Onboarding starts with the registry's recommended subscriptions pre-selected. */
export function initialSelections(): SignalSelections {
  return new Map(RECOMMENDED_SIGNAL_SUBSCRIPTIONS.map((r) => [r.signalType, r.priority]));
}

/** Toggle a type on (at its default priority, keeping any existing choice) or off. */
export function toggleSelection(
  current: SignalSelections,
  signalType: SignalType,
  checked: boolean,
): SignalSelections {
  const next = new Map(current);
  if (checked) {
    if (!next.has(signalType)) next.set(signalType, defaultPriorityFor(signalType));
  } else {
    next.delete(signalType);
  }
  return next;
}

/** Set the priority of an already-selected type. No-op if the type is not selected. */
export function setSelectionPriority(
  current: SignalSelections,
  signalType: SignalType,
  priority: SignalPriority,
): SignalSelections {
  const next = new Map(current);
  if (next.has(signalType)) next.set(signalType, priority);
  return next;
}

/** How many of a stage group's types are currently selected (for the section badge). */
export function countSelectedInGroup(current: SignalSelections, group: SignalTypeGroup): number {
  return group.signalTypes.reduce((count, type) => (current.has(type) ? count + 1 : count), 0);
}
